import { useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { IButton } from "libs/design-system/src/types/Button/button";
import { Pen, TrashCan } from "@unified-checkout/design-system/icons/index";
import { SkeletonBase, SkeletonLoader } from "@unified-checkout/design-system";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import type { IRoleManagementLocationProps } from "@app/dashboard/types/innerLayout/user_management";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { useLangStore } from "@app/dashboard/store/lang";
import { getRoles } from "@app/dashboard/services/inner_layout/user_management/roles/get-roles";
import { InfoChip } from "@app/dashboard/components/inner_layout/InfoChip";
import { ComponentError } from "@app/dashboard/components/shared/ComponentError";
import { DeleteModal as DeleteRoleModal } from "../../components/modals/DeleteModal";
import styles from "./manage-roles.module.scss";


const CHIP_WIDTH = "21rem";
const CHIP_HEIGHT = "6.5rem";


export const ManageRoles = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const navigate = useNavigate();
  const [deleteModalProps, setDeleteModalProps] = useState({
    isOpen: false,
    roleId: ''
  });

  const {
    isLoading,
    isRefetching,
    isError,
    error,
    data: userRoles
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_ROLES, lang],
    queryFn: () => getRoles({}).then(res => res.data.data)
  });

  const errMsg = ((error as AxiosError)?.response?.data as any)?.message || t('errors.somethingWentWrong');

  const usersAssignedText = (count: number) => {
    const text = count === 1 ?
      t('innerLayout.userManagementPage.userAssigned')
      :
      t('innerLayout.userManagementPage.usersAssigned')

    return `${count} ${text}`;
  };

  return (
    <div className={styles["manage-roles"]}>

      {
        isLoading /* || isRefetching */ ?
          <div className={styles["manage-roles__loader"]}>
            <>
              <SkeletonBase width="10em" height="2.25rem" />

              <div className={styles["manage-roles__loader__chips"]}>
                {
                  Array.from({ length: 6 }).map((_, index) =>
                    <SkeletonLoader
                      key={`manage-roles-info-chip-${index}-loader`}
                      width={CHIP_WIDTH}
                      height={CHIP_HEIGHT}
                      noOfRows={2}
                    />
                  )
                }
              </div>
            </>
          </div>
          :
          isError ?
            <ComponentError
              text={errMsg}
            />
            :
            (
              Object.entries(userRoles || {})?.map((rolesGroups) => {
                const [rolesGroupTitle, rolesGroup] = rolesGroups as [string, []];

                return (
                  !!rolesGroup.length &&
                  <div className={styles["manage-roles__section"]} key={`manage-role-section-${rolesGroupTitle}`}>
                    <div className={styles["manage-roles__section__title"]}>
                      {rolesGroupTitle === 'system_roles' ? t('innerLayout.userManagementPage.systemRoles') : t('innerLayout.userManagementPage.customRoles')}
                    </div>
                    <div className={styles["manage-roles__section__info-chips"]}>
                      {
                        rolesGroup?.map((role: any, index) => (
                          <InfoChip
                            key={`manage-roles-chip-${index}`}
                            width={CHIP_WIDTH}
                            text={role.title}
                            hintText={usersAssignedText(role.user_count)}
                            btnsProps={
                              [
                                {
                                  leadingIcon: Pen,
                                  color: 'secondary',
                                  onClickHandler: () => {
                                    const path = generatePath(INNER_ROUTES.ROLE_DETAILS, { id: role.id });
                                    navigate(path, {
                                      state: { pageType: 'role_details' } as IRoleManagementLocationProps,
                                    });
                                  }
                                },
                                ...(
                                  (role.user_count === 0 && rolesGroupTitle === 'custom_roles') ?
                                    [{
                                      leadingIcon: TrashCan,
                                      color: 'danger',
                                      onClickHandler: () => {
                                        setDeleteModalProps({
                                          isOpen: true,
                                          roleId: role.id
                                        });
                                      }
                                    }]
                                    :
                                    []
                                )
                              ] as IButton[]
                            }
                          />
                        ))
                      }
                    </div>
                  </div>
                )
              })
            )
      }

      <DeleteRoleModal
        type='deleteRole'
        roleId={deleteModalProps.roleId || ''}
        open={deleteModalProps.isOpen}
        setOpen={(open) => setDeleteModalProps((prevState) => ({ ...prevState, isOpen: open }))}
      />

    </div>
  )
}